import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { juntarClasses } from '../../componentes/ui';
import {
  editarAtividade,
  formatarDataIso,
  hojeIso,
  rotuloDoTipo,
  situacaoDoPrazo,
  type AtividadeDoCrm,
} from '../../servicos/crm';
import { ErroDaApi } from '../ErroDaApi';
import {
  Arrastavel,
  CelulaDoDia,
  DIAS_DA_SEMANA,
  NavegacaoDoMes,
  PREFIXO_DO_DIA,
  colisaoDoCalendario,
  tituloDoMes,
  type DiaDaGrade,
  type MesDoCalendario,
} from './calendario';

type Situacao = 'concluida' | 'atrasada' | 'hoje' | 'aberta';

function situacaoDe(a: AtividadeDoCrm): Situacao {
  if (a.concluidaEm !== null) return 'concluida';
  const prazo = situacaoDoPrazo(a.prazo);
  return prazo === 'vencida' ? 'atrasada' : prazo === 'hoje' ? 'hoje' : 'aberta';
}

/** As cores da lista de Atividades: atrasada em vermelho, hoje em laranja. */
const ESTILO: Readonly<Record<Situacao, string>> = {
  atrasada: 'border-erro/40 bg-erro/5 text-erro',
  hoje: 'border-atencao/40 bg-atencao/10 text-atencao',
  aberta: 'border-borda bg-superficie-elevada text-conteudo-primario',
  concluida: 'border-borda/60 bg-superficie-base text-conteudo-sutil',
};

const LEGENDA: readonly (readonly [Situacao, string])[] = [
  ['atrasada', 'Atrasada'],
  ['hoje', 'Hoje'],
  ['aberta', 'Em aberto'],
  ['concluida', 'Concluída'],
];

/**
 * Atividades por prazo, num mês — a mesma grade do calendário do Pipeline.
 *
 * Mostra abertas e concluídas: a cor diz a situação, como na lista. Clicar
 * abre a atividade para editar — concluir, trocar o responsável. Quem busca o
 * que fazer hoje continua com a lista "Hoje e atrasadas"; o calendário
 * responde "como está a semana".
 *
 * ## Arrastar muda o prazo
 *
 * Soltar uma atividade em aberto noutro dia grava aquele dia como prazo. A
 * tela muda na hora (`ajustes`) e a gravação vai atrás; se o servidor
 * recusar, a atividade volta para onde estava. A concluída não se arrasta:
 * o prazo dela é história, não agenda.
 */
export function CalendarioDeAtividades(props: {
  readonly mes: MesDoCalendario;
  readonly dias: readonly DiaDaGrade[];
  readonly atividades: readonly AtividadeDoCrm[];
  readonly carregando: boolean;
  readonly aoMudarMes: (delta: number) => void;
  readonly aoIrParaHoje: () => void;
  readonly aoAbrir: (atividade: AtividadeDoCrm) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  /** Prazos já trocados na tela e ainda não confirmados pelo servidor. */
  const [ajustes, setAjustes] = useState<Readonly<Record<string, string>>>({});
  const [ativo, setAtivo] = useState<string | null>(null);

  // Ajuste que o servidor já confirmou sai da lista; o que ainda não chegou
  // fica, para uma resposta atrasada não devolver a atividade ao dia antigo.
  useEffect(() => {
    const doServidor = new Map(props.atividades.map((a) => [a.id, a.prazo]));
    setAjustes((atuais) => {
      const pendentes = Object.entries(atuais).filter(
        ([id, prazo]) => doServidor.get(id) !== prazo,
      );
      return pendentes.length === Object.keys(atuais).length
        ? atuais
        : Object.fromEntries(pendentes);
    });
  }, [props.atividades]);

  const reagendar = useMutation({
    mutationFn: (m: { id: string; prazo: string }) => editarAtividade(m.id, { prazo: m.prazo }),
    onError: (_erro, m) =>
      setAjustes((atuais) =>
        Object.fromEntries(Object.entries(atuais).filter(([id]) => id !== m.id)),
      ),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['crm'] }),
  });

  /** As atividades com o prazo que a tela mostra — o ajustado, se houver. */
  const atividades = useMemo(
    () =>
      props.atividades.map((a) => {
        const ajustado = ajustes[a.id];
        return ajustado === undefined ? a : { ...a, prazo: ajustado };
      }),
    [props.atividades, ajustes],
  );
  const porDia = useMemo(() => {
    const mapa = new Map<string, AtividadeDoCrm[]>();
    for (const a of atividades) mapa.set(a.prazo, [...(mapa.get(a.prazo) ?? []), a]);
    return mapa;
  }, [atividades]);

  const sensores = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // No toque, segurar um instante levanta a atividade; deslizar sem segurar rola.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const aoSoltar = (evento: DragEndEvent): void => {
    setAtivo(null);
    const alvo = evento.over === null ? null : String(evento.over.id);
    const atividade = atividades.find((a) => a.id === String(evento.active.id));
    if (alvo === null || atividade === undefined || !alvo.startsWith(PREFIXO_DO_DIA)) return;

    const novoPrazo = alvo.slice(PREFIXO_DO_DIA.length);
    if (novoPrazo === atividade.prazo) return;
    setAjustes((atuais) => ({ ...atuais, [atividade.id]: novoPrazo }));
    reagendar.mutate({ id: atividade.id, prazo: novoPrazo });
  };

  const tituloDe = (id: string | number): string =>
    atividades.find((a) => a.id === String(id))?.titulo ?? 'Atividade';
  const diaDe = (id: string | number): string =>
    `dia ${formatarDataIso(String(id).slice(PREFIXO_DO_DIA.length))}`;

  const hoje = hojeIso();
  const doMes = atividades.filter((a) =>
    props.dias.some((d) => d.doMes && d.iso === a.prazo),
  ).length;
  const atividadeAtiva = ativo === null ? undefined : atividades.find((a) => a.id === ativo);

  return (
    <DndContext
      sensors={sensores}
      collisionDetection={colisaoDoCalendario}
      onDragStart={(e) => setAtivo(String(e.active.id))}
      onDragEnd={aoSoltar}
      onDragCancel={() => setAtivo(null)}
      accessibility={{
        announcements: {
          onDragStart: ({ active }) => `${tituloDe(active.id)} levantada.`,
          onDragOver: ({ active, over }) =>
            over === null ? undefined : `${tituloDe(active.id)} sobre o ${diaDe(over.id)}.`,
          onDragEnd: ({ active, over }) =>
            over === null
              ? `${tituloDe(active.id)} solta fora do calendário. O prazo não mudou.`
              : `${tituloDe(active.id)} agora no ${diaDe(over.id)}.`,
          onDragCancel: ({ active }) => `Arrasto cancelado. ${tituloDe(active.id)} voltou.`,
        },
        screenReaderInstructions: {
          draggable:
            'Para mudar o prazo, aperte espaço para levantar a atividade, use as setas para levá-la ao dia e espaço de novo para soltar. Esc cancela.',
        },
      }}
    >
      <div className="flex flex-col gap-4">
        {reagendar.isError && (
          <ErroDaApi titulo="Não foi possível mudar o prazo" erro={reagendar.error} />
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold capitalize tracking-tight text-conteudo-primario">
              {tituloDoMes(props.mes)}
            </h2>
            <p className="text-xs text-conteudo-sutil">
              {props.carregando
                ? 'Carregando as atividades…'
                : doMes === 0
                  ? 'Nenhuma atividade com prazo neste mês.'
                  : `${doMes} ${doMes === 1 ? 'atividade' : 'atividades'} com prazo neste mês. Clique para abrir; arraste uma em aberto para outro dia para mudar o prazo.`}
            </p>
          </div>
          <NavegacaoDoMes aoMudar={props.aoMudarMes} aoIrParaHoje={props.aoIrParaHoje} />
        </div>

        {/* A grade rola na horizontal no celular, em vez de espremer sete colunas. */}
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div className="grid min-w-[48rem] grid-cols-7 overflow-hidden rounded-painel border border-borda">
            {DIAS_DA_SEMANA.map((d) => (
              <div
                key={d}
                className="border-b border-borda bg-superficie-tabelaTopo px-2 py-2 text-xs font-semibold uppercase tracking-wide text-conteudo-tabelaTopo"
              >
                {d}
              </div>
            ))}
            {props.dias.map((d, i) => (
              <CelulaDoDia
                key={d.iso}
                iso={d.iso}
                dia={d.dia}
                doMes={d.doMes}
                hoje={d.iso === hoje}
                bordaDireita={i % 7 !== 6}
                bordaInferior={i < 35}
              >
                {(porDia.get(d.iso) ?? []).map((a) =>
                  a.concluidaEm === null ? (
                    <Arrastavel key={a.id} id={a.id} descricao="atividade arrastável">
                      <ItemDoCalendario atividade={a} aoAbrir={() => props.aoAbrir(a)} />
                    </Arrastavel>
                  ) : (
                    <ItemDoCalendario key={a.id} atividade={a} aoAbrir={() => props.aoAbrir(a)} />
                  ),
                )}
              </CelulaDoDia>
            ))}
          </div>
        </div>

        <ul
          aria-label="Legenda"
          className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-conteudo-sutil"
        >
          {LEGENDA.map(([situacao, rotulo]) => (
            <li key={situacao} className="flex items-center gap-1.5">
              <span className={juntarClasses('h-3 w-3 rounded-sm border', ESTILO[situacao])} />
              {rotulo}
            </li>
          ))}
        </ul>
      </div>

      <DragOverlay>
        {atividadeAtiva === undefined ? null : (
          <div className="w-44 rounded-card shadow-cartao">
            <ItemDoCalendario atividade={atividadeAtiva} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function ItemDoCalendario(props: {
  readonly atividade: AtividadeDoCrm;
  /** Sem ele, é a cópia que voa no arrasto: não abre nada. */
  readonly aoAbrir?: () => void;
}): JSX.Element {
  const a = props.atividade;
  const situacao = situacaoDe(a);
  const empresa = a.card.nomeFantasia?.trim() || a.card.razaoSocial;
  const quando = a.hora === null ? '' : `${a.hora} · `;
  const rotuloDaSituacao = LEGENDA.find(([s]) => s === situacao)?.[1] ?? '';

  return (
    <button
      type="button"
      onClick={props.aoAbrir}
      tabIndex={props.aoAbrir === undefined ? -1 : undefined}
      title={`${rotuloDoTipo(a.tipo)} · ${quando}${a.titulo} — ${empresa} · ${a.responsavel.nome} · ${rotuloDaSituacao}`}
      aria-label={`${a.titulo}, ${empresa}, ${rotuloDaSituacao}. Abrir para editar.`}
      className={juntarClasses(
        'flex w-full flex-col rounded-card border px-1.5 py-1 text-left text-xs transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-acento-ciano',
        ESTILO[situacao],
      )}
    >
      <span
        className={juntarClasses(
          'truncate font-medium',
          situacao === 'concluida' && 'line-through',
        )}
      >
        {situacao === 'concluida' && '✓ '}
        {quando}
        {a.titulo}
      </span>
      <span className="truncate text-[0.7rem] text-conteudo-sutil">{empresa}</span>
    </button>
  );
}
